import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Checkbox, Flex, Form, Input } from 'antd';
import useForm from '../../hooks/utility/useForm';
import Document from '../pdf/Document';
import useApiClient from '../../hooks/api/useApiClient';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';
import useHelpers from '../../hooks/context/useHelpers';

export default function SignStep({ contract, backButton, onSuccess }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  const { ServiceContractsManager } = useApiClient();
  const { getValue } = useBusinessProfile();
  const { formatDate } = useHelpers();

  const [preview, setPreview] = useState(null);
  const [signed, setSigned] = useState(false);
  const [page, setPage] = useState(1);
  const { data, update } = useForm({
    agreement: false,
    signer_first_name: contract?.signer_first_name ?? '',
    signer_last_name: contract?.signer_last_name ?? '',
    send_to_email: true,
  });

  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    loadPreview({});
  }, []);

  const loadPreview = async (body = null) => {
    setLoadingPreview(true);
    const { success, data: responseData } = await ServiceContractsManager.preview(contract?.id, body ?? data);
    setLoadingPreview(false);

    if (!success) {
      return toast(t('toasts.failed_to_preview_contract'));
    }

    setPreview(responseData);
  };

  const sign = async () => {
    setLoading(true);
    toast.promise(
      new Promise((resolve, reject) => {
        ServiceContractsManager.sign(contract?.id, data)
          .then((response) => {
            if (!response?.success) {
              return reject();
            }

            onSuccess(response.data);

            return resolve();
          })
          .finally(() => setLoading(false));
      }),
      {
        pending: t('toasts.contract_signing_pending'),
        success: t('toasts.contract_got_signed'),
        error: t('toasts.failed_to_sign_contract'),
      },
    );
  };

  return (
    <div className="mt-lg">
      <Flex justify="center">
        <Document file={preview} loading={loadingPreview} defaultPage={page} onChangePage={setPage} />
      </Flex>
      {signed && (
        <div className="flex mt-lg">
          <p className="my-auto">
            {t('labels.name')}
            {': '}
            {contract?.signed_by}
          </p>
          <p className="ml-xs my-auto">|</p>
          <p className="my-auto ml-xs">
            {t('labels.date')}
            {': '}
            {formatDate(new Date())}
          </p>
          <Button
            onClick={() => {
              setSigned(false);
              loadPreview({});
            }}
            className="ml-lg"
          >
            {t('buttons.redo')}
          </Button>
        </div>
      )}
      {!signed && (
        <Form layout="vertical" className="mt-lg">
          <Form.Item>
            <Checkbox id="agreement" checked={data.agreement} onChange={(e) => update('agreement', e.target.checked)}>
              {t('form.agreement.label')}
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Checkbox
              id="send_to_email"
              checked={data.send_to_email}
              onChange={(e) => update('send_to_email', e.target.checked)}
            >
              {t('form.send_to_email.label', { email: getValue('owner_email') })}
            </Checkbox>
          </Form.Item>
          <Form.Item label={t('form.signer_first_name.label')}>
            <Input
              value={data.signer_first_name}
              id="signer_first_name"
              onChange={(e) => update('signer_first_name', e.target.value)}
              placeholder={t('form.signer_first_name.placeholder')}
              className="w-3/5"
            />
          </Form.Item>
          <Form.Item label={t('form.signer_last_name.label')}>
            <Input
              value={data.signer_last_name}
              id="signer_last_name"
              onChange={(e) => update('signer_last_name', e.target.value)}
              placeholder={t('form.signer_last_name.placeholder')}
              className="w-3/5"
            />
          </Form.Item>
        </Form>
      )}
      <Flex justify="space-between" gap="small">
        <Button
          hidden={signed}
          type="primary"
          disabled={!data.agreement || !data.signer_first_name?.trim() || !data.signer_last_name?.trim()}
          onClick={() => {
            setSigned(true);
            loadPreview();
          }}
        >
          {t('buttons.sign')}
        </Button>
        <Flex justify="end" gap="small" className="ml-auto">
          {backButton}
          <Button type="primary" loading={loading} disabled={!signed} onClick={sign}>
            {t('buttons.submit')}
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}
